import { useMemo, useState } from "react";
import {
  Bar, BarChart, CartesianGrid, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from "recharts";
import { fmtN } from "../../lib/funnel/helpers";
import { PRIMARY, GOLD, FG1, FG3, MUTED, BORDER, VIOLET } from "../../lib/funnel/theme";
import { SectionCardHeader } from "../../components/ChartCard";
import { InfoButton } from "../../lib/funnel/InfoButton";
import type { FilterPillSpec } from "../../components/FilterPills";
import type { BaselineRow } from "./sql";

type EventKey =
  | "calendar"
  | "transcript"
  | "first_synopsis"
  | "second_synopsis"
  | "custom_template"
  | "chat_meeting"
  | "chat_client"
  | "document"
  | "task"
  | "client"
  | "form"
  | "teammate"
  | "paid"
  | "plus"
  | "pro";

type EventSpec = { key: EventKey; label: string; dateOf: (u: BaselineRow) => string | null };

const EVENTS: EventSpec[] = [
  { key: "calendar",         label: "First calendar connect",  dateOf: (u) => u.first_calendar_date },
  { key: "transcript",       label: "First transcript",        dateOf: (u) => u.first_transcript_date },
  { key: "first_synopsis",   label: "First summary",           dateOf: (u) => u.first_synopsis_date },
  { key: "second_synopsis",  label: "Second summary",          dateOf: (u) => u.second_synopsis_date },
  { key: "custom_template",  label: "First custom template",   dateOf: (u) => u.first_custom_template_date },
  { key: "chat_meeting",     label: "First Ask Marloo (meeting)", dateOf: (u) => u.first_chat_meeting_date },
  { key: "chat_client",      label: "First Ask Marloo (client)",  dateOf: (u) => u.first_chat_client_date },
  { key: "document",         label: "First document",          dateOf: (u) => u.first_document_date },
  { key: "task",             label: "First task",              dateOf: (u) => u.first_task_date },
  { key: "client",           label: "First client",            dateOf: (u) => u.first_client_date },
  { key: "form",             label: "First form",              dateOf: (u) => u.first_form_date },
  { key: "teammate",         label: "First teammate join",     dateOf: (u) => u.first_teammate_join_date },
  { key: "paid",             label: "Converted to paid",       dateOf: (u) => u.paid_converted_date_utc },
  { key: "plus",             label: "Converted to Plus",       dateOf: (u) => u.first_plus_date },
  { key: "pro",              label: "Converted to Pro",        dateOf: (u) => u.first_pro_date },
];

type Bucket = "15m" | "hour" | "day" | "week";
type Window = "24h" | "7d" | "30d" | "60d" | "90d";

const BUCKET_LABEL: Record<Bucket, string> = { "15m": "15 min", hour: "Hourly", day: "Daily", week: "Weekly" };
const WINDOW_LABEL: Record<Window, string> = { "24h": "First 24h", "7d": "First 7 days", "30d": "First 30 days", "60d": "First 60 days", "90d": "First 90 days" };

const BUCKET_DAYS: Record<Bucket, number> = {
  "15m": 15 / (60 * 24),
  hour: 1 / 24,
  day: 1,
  week: 7,
};
const WINDOW_DAYS: Record<Window, number> = {
  "24h": 1,
  "7d": 7,
  "30d": 30,
  "60d": 60,
  "90d": 90,
};

function bucketLabelFor(bucket: Bucket, i: number): string {
  switch (bucket) {
    case "15m": {
      const startMin = i * 15;
      const h = Math.floor(startMin / 60);
      const m = startMin % 60;
      return `${h}h${m === 0 ? "" : `:${String(m).padStart(2, "0")}`}`;
    }
    case "hour": return `${i}h`;
    case "day":  return `D${i}`;
    case "week": return `W${i + 1}`;
  }
}

function daysFromSignup(u: BaselineRow, ev: EventSpec): number | null {
  const date = ev.dateOf(u);
  if (!date) return null;
  const signupTs = new Date(u.signup_date_utc).getTime();
  const eventTs = new Date(date).getTime();
  if (!Number.isFinite(signupTs) || !Number.isFinite(eventTs)) return null;
  const days = (eventTs - signupTs) / 86400_000;
  if (!Number.isFinite(days) || days < 0) return null;
  return days;
}

function percentile(sorted: number[], p: number): number | null {
  if (sorted.length === 0) return null;
  const idx = (sorted.length - 1) * p;
  const lo = Math.floor(idx);
  const hi = Math.ceil(idx);
  const a = sorted[lo];
  const b = sorted[hi];
  if (a == null || b == null) return null;
  if (lo === hi) return a;
  return a + (b - a) * (idx - lo);
}

function mean(values: number[]): number | null {
  if (values.length === 0) return null;
  let s = 0;
  for (const v of values) s += v;
  return s / values.length;
}

function fmtDays(days: number | null): string {
  if (days == null) return "-";
  if (days < 1 / 24) return `${Math.round(days * 24 * 60)}m`;
  if (days < 1) return `${Math.round(days * 24)}h`;
  if (days < 14) return `${days.toFixed(1)}d`;
  if (days < 60) return `${Math.round(days)}d`;
  return `${(days / 7).toFixed(1)}w`;
}

export function FirstEventHistogram({
  users,
  pagePills,
  compareUsers,
  comparePills,
}: {
  users: BaselineRow[];
  pagePills?: FilterPillSpec[];
  compareUsers?: BaselineRow[] | null;
  comparePills?: FilterPillSpec[];
}) {
  const compareOn = !!compareUsers;
  const [eventKey, setEventKey] = useState<EventKey>("first_synopsis");
  const [bucket, setBucket] = useState<Bucket>("day");
  const [windw, setWindw] = useState<Window>("30d");
  const [showAbs, setShowAbs] = useState(false);
  const ev = EVENTS.find((e) => e.key === eventKey) ?? EVENTS[2]!;

  const bucketSize = BUCKET_DAYS[bucket];
  const windowDays = WINDOW_DAYS[windw];
  const bucketCount = Math.ceil(windowDays / bucketSize);

  function statsOf(uList: BaselineRow[]) {
    const values: number[] = [];
    for (const u of uList) {
      const d = daysFromSignup(u, ev);
      if (d != null) values.push(d);
    }
    values.sort((a, b) => a - b);
    const counts: number[] = Array.from({ length: bucketCount }, () => 0);
    let overflow = 0;
    let inWindow = 0;
    for (const v of values) {
      const bi = Math.floor(v / bucketSize);
      if (bi >= 0 && bi < bucketCount) counts[bi] = (counts[bi] ?? 0) + 1;
      else if (bi >= bucketCount) overflow += 1;
      // Align "Reached in {window}" with the KPI cards, which apply
      // floor(days) <= windowDays (via daysBetween). The bucket-overflow path
      // above uses continuous days strictly < windowDays, which under-counts
      // users whose event landed in (windowDays, windowDays+1) — same root
      // cause as the Plus@30d KPI-vs-histogram 2-user gap.
      if (Math.floor(v) <= windowDays) inWindow += 1;
    }
    return {
      values,
      counts,
      overflow,
      inWindow,
      n: values.length,
      cohortN: uList.length,
      median: percentile(values, 0.5),
      mean: mean(values),
      p90: percentile(values, 0.9),
      p95: percentile(values, 0.95),
    };
  }

  const baseStats = useMemo(() => statsOf(users), [users, ev, bucket, windw]);
  const compareStats = useMemo(() => compareUsers ? statsOf(compareUsers) : null, [compareUsers, ev, bucket, windw]);

  const data = useMemo(() => Array.from({ length: bucketCount }, (_, i) => {
    const baseCount = baseStats.counts[i] ?? 0;
    const compCount = compareStats?.counts[i] ?? 0;
    const basePct = baseStats.n === 0 ? 0 : (baseCount / baseStats.n) * 100;
    const compPct = !compareStats || compareStats.n === 0 ? 0 : (compCount / compareStats.n) * 100;
    return {
      label: bucketLabelFor(bucket, i),
      baseCount,
      basePct,
      compCount,
      compPct,
    };
  }), [baseStats, compareStats, bucket, bucketCount]);

  const medianBucketIdx = baseStats.median != null ? Math.floor(baseStats.median / bucketSize) : null;
  const p90BucketIdx = baseStats.p90 != null ? Math.floor(baseStats.p90 / bucketSize) : null;
  const medianLabel = medianBucketIdx != null && medianBucketIdx >= 0 && medianBucketIdx < bucketCount ? data[medianBucketIdx]?.label : null;
  const p90Label = p90BucketIdx != null && p90BucketIdx >= 0 && p90BucketIdx < bucketCount ? data[p90BucketIdx]?.label : null;

  const pills: FilterPillSpec[] = [
    ...(pagePills ?? []),
    ...(compareOn ? [{ kind: "mode" as const, label: "vs comparison" }, ...(comparePills ?? [])] : []),
    { kind: "mode" as const, label: ev.label },
    { kind: "mode" as const, label: WINDOW_LABEL[windw] },
    { kind: "mode" as const, label: `${BUCKET_LABEL[bucket]} bucket` },
  ];

  const useRaw = bucket === "15m" || bucket === "hour";
  const windowDisabled = (key: Window) => useRaw && (key === "30d" || key === "60d" || key === "90d");

  return (
    <section className="card">
      <SectionCardHeader
        title={
          <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
            Time-to-first-event distribution
            <InfoButton label="How to read this">
              <p style={{ margin: "0 0 6px" }}>
                Histogram of how long after sign-up users reach a single milestone (first summary, first custom
                template, first paid conversion, etc.). Only users who actually reached the milestone are included
                in the distribution; the reach % shows how much of the cohort that is.
              </p>
              <p style={{ margin: "0 0 6px" }}>
                <strong>Median</strong> (p50): half the users who reached the milestone got there faster than this.
                Best-in-class self-serve time-to-value is 1-2 days for the core "aha" event.
                <br />
                <strong>p90</strong>: 90% of reachers got there within this time, exposing the stalled long tail the
                median hides.
                <br />
                <strong>Mean</strong>: arithmetic average. Pulled up by the long tail, so usually higher than the
                median.
              </p>
              <p style={{ margin: 0, color: FG3 }}>
                Use the <strong>bucket</strong> (15m / Hour / Day / Week) and <strong>window</strong> (24h / 7d / 30d /
                60d / 90d) controls to zoom in on the early-activation curve or zoom out to the long tail. Stats are
                computed from the full reacher distribution, not the windowed view.
              </p>
            </InfoButton>
          </span>
        }
        subtitle={
          baseStats.n === 0
            ? `No users in the cohort have reached '${ev.label.toLowerCase()}' yet.`
            : `${fmtN(baseStats.n)} of ${fmtN(baseStats.cohortN)} users (${((baseStats.n / Math.max(1, baseStats.cohortN)) * 100).toFixed(1)}%) reached '${ev.label.toLowerCase()}'. Median ${fmtDays(baseStats.median)}, p90 ${fmtDays(baseStats.p90)}.${baseStats.overflow > 0 ? ` ${fmtN(baseStats.overflow)} reachers fall outside the ${WINDOW_LABEL[windw].toLowerCase()} window.` : ""}`
        }
        filterPills={pills}
        headerExtra={
          <div style={{ display: "inline-flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
            <select
              value={eventKey}
              onChange={(e) => setEventKey(e.target.value as EventKey)}
              style={{
                border: `1px solid ${MUTED}`, borderRadius: 6, padding: "4px 8px",
                fontSize: 12, background: "var(--white)", color: FG1, cursor: "pointer",
              }}
            >
              {EVENTS.map((e) => (
                <option key={e.key} value={e.key}>{e.label}</option>
              ))}
            </select>
            <PillGroup
              value={bucket}
              onChange={setBucket}
              options={[
                { key: "15m", label: "15m" },
                { key: "hour", label: "Hour" },
                { key: "day", label: "Day" },
                { key: "week", label: "Week" },
              ]}
            />
            <PillGroup
              value={windw}
              onChange={(v) => setWindw(v as Window)}
              options={[
                { key: "24h", label: "24h" },
                { key: "7d", label: "7d" },
                { key: "30d", label: "30d" },
                { key: "60d", label: "60d" },
                { key: "90d", label: "90d" },
              ]}
              disabled={(opt) => windowDisabled(opt.key as Window)}
            />
            <PillGroup
              value={showAbs ? "abs" : "pct"}
              onChange={(v) => setShowAbs(v === "abs")}
              options={[
                { key: "pct", label: "%" },
                { key: "abs", label: "#" },
              ]}
            />
          </div>
        }
      />
      <div className="card-body" style={{ display: "grid", gridTemplateColumns: "1fr 200px", gap: 16, alignItems: "stretch" }}>
        <div style={{ height: 320 }}>
          {baseStats.n === 0 ? (
            <div style={{ color: FG3, fontSize: 12, display: "flex", alignItems: "center", height: "100%" }}>
              No reachers in this cohort.
            </div>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data} margin={{ top: 28, right: 16, bottom: 24, left: 8 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={MUTED} opacity={0.3} vertical={false} />
                <XAxis dataKey="label" tick={{ fontSize: 11, fill: FG3 }} interval="preserveStartEnd" />
                <YAxis tick={{ fontSize: 11, fill: FG3 }} tickFormatter={(v) => showAbs ? fmtN(Math.round(Number(v))) : `${Math.round(Number(v))}%`} width={showAbs ? 48 : 36} />
                <Tooltip
                  cursor={{ fill: "rgba(47,122,138,0.06)" }}
                  content={(props) => {
                    const { active, payload, label } = props as unknown as { active?: boolean; payload?: { payload: typeof data[number] }[]; label?: string };
                    if (!active || !payload || payload.length === 0) return null;
                    const row = payload[0]?.payload;
                    if (!row) return null;
                    return (
                      <div style={{ background: "var(--white)", border: "1px solid var(--border-default)", borderRadius: 7, padding: "8px 10px", fontSize: 12, boxShadow: "var(--shadow-md)", lineHeight: 1.4 }}>
                        <div style={{ fontWeight: 600, marginBottom: 2 }}>{label}</div>
                        <div style={{ color: PRIMARY }}>
                          Baseline: <strong>{showAbs ? fmtN(row.baseCount) : `${row.basePct.toFixed(1)}%`}</strong>
                          <span style={{ color: FG3, marginLeft: 6, fontSize: 11 }}>{showAbs ? `(${row.basePct.toFixed(1)}%)` : `(${fmtN(row.baseCount)} users)`}</span>
                        </div>
                        {compareStats && (
                          <div style={{ color: GOLD, marginTop: 2 }}>
                            Comparison: <strong>{showAbs ? fmtN(row.compCount) : `${row.compPct.toFixed(1)}%`}</strong>
                            <span style={{ color: FG3, marginLeft: 6, fontSize: 11 }}>{showAbs ? `(${row.compPct.toFixed(1)}%)` : `(${fmtN(row.compCount)} users)`}</span>
                          </div>
                        )}
                      </div>
                    );
                  }}
                />
                {medianLabel != null && (
                  <ReferenceLine x={medianLabel} stroke={VIOLET} strokeDasharray="3 3" label={{ value: "median", position: "top", fontSize: 10, fill: VIOLET }} />
                )}
                {p90Label != null && p90Label !== medianLabel && (
                  <ReferenceLine x={p90Label} stroke={VIOLET} strokeDasharray="3 3" opacity={0.5} label={{ value: "p90", position: "top", fontSize: 10, fill: VIOLET }} />
                )}
                <Bar dataKey={showAbs ? "baseCount" : "basePct"} name="Baseline" fill={PRIMARY} radius={[4, 4, 0, 0]} />
                {compareStats && <Bar dataKey={showAbs ? "compCount" : "compPct"} name="Comparison" fill={GOLD} radius={[4, 4, 0, 0]} />}
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 8, fontSize: 12 }}>
          <div style={{ color: FG3, fontSize: 11, textTransform: "uppercase", letterSpacing: "0.05em", fontWeight: 600 }}>
            Stats
          </div>
          <StatCard label="Reached" baseValue={`${baseStats.n === 0 ? 0 : ((baseStats.n / Math.max(1, baseStats.cohortN)) * 100).toFixed(1)}%`} baseSub={`${fmtN(baseStats.n)} / ${fmtN(baseStats.cohortN)}`} compareValue={compareStats ? `${compareStats.n === 0 ? 0 : ((compareStats.n / Math.max(1, compareStats.cohortN)) * 100).toFixed(1)}%` : null} compareSub={compareStats ? `${fmtN(compareStats.n)} / ${fmtN(compareStats.cohortN)}` : null} />
          {(() => {
            const baseInWindow = baseStats.inWindow;
            const compInWindow = compareStats ? compareStats.inWindow : 0;
            return (
              <StatCard
                label={`Reached in ${windw}`}
                baseValue={`${baseStats.cohortN === 0 ? 0 : ((baseInWindow / Math.max(1, baseStats.cohortN)) * 100).toFixed(1)}%`}
                baseSub={`${fmtN(baseInWindow)} / ${fmtN(baseStats.cohortN)}`}
                compareValue={compareStats ? `${compareStats.cohortN === 0 ? 0 : ((compInWindow / Math.max(1, compareStats.cohortN)) * 100).toFixed(1)}%` : null}
                compareSub={compareStats ? `${fmtN(compInWindow)} / ${fmtN(compareStats.cohortN)}` : null}
              />
            );
          })()}
          <StatCard label="Median" baseValue={fmtDays(baseStats.median)} compareValue={compareStats ? fmtDays(compareStats.median) : null} />
          <StatCard label="Mean" baseValue={fmtDays(baseStats.mean)} compareValue={compareStats ? fmtDays(compareStats.mean) : null} />
          <StatCard label="p90" baseValue={fmtDays(baseStats.p90)} compareValue={compareStats ? fmtDays(compareStats.p90) : null} />
          <StatCard label="p95" baseValue={fmtDays(baseStats.p95)} compareValue={compareStats ? fmtDays(compareStats.p95) : null} />
        </div>
      </div>
    </section>
  );
}

function StatCard({
  label, baseValue, baseSub, compareValue, compareSub,
}: {
  label: string;
  baseValue: string;
  baseSub?: string;
  compareValue: string | null;
  compareSub?: string | null;
}) {
  return (
    <div style={{ padding: "8px 10px", border: `1px solid ${BORDER}`, borderRadius: 6 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
        <span style={{ fontWeight: 600 }}>{label}</span>
        <span style={{ color: PRIMARY, fontWeight: 600, fontSize: 16 }}>{baseValue}</span>
      </div>
      {baseSub && (
        <div style={{ color: FG3, fontSize: 10, marginTop: 2, textAlign: "right" }}>{baseSub}</div>
      )}
      {compareValue && (
        <div style={{ marginTop: 6, paddingTop: 6, borderTop: `1px dashed ${GOLD}66`, display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
          <span style={{ color: GOLD, fontSize: 10, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em" }}>Cmp</span>
          <div style={{ textAlign: "right" }}>
            <div style={{ color: GOLD, fontWeight: 600, fontSize: 14 }}>{compareValue}</div>
            {compareSub && <div style={{ color: FG3, fontSize: 10 }}>{compareSub}</div>}
          </div>
        </div>
      )}
    </div>
  );
}

function PillGroup<T extends string>({
  value, onChange, options, disabled,
}: {
  value: T;
  onChange: (v: T) => void;
  options: { key: T; label: string }[];
  disabled?: (opt: { key: T; label: string }) => boolean;
}) {
  return (
    <div role="group" style={{ display: "inline-flex", border: `1px solid ${MUTED}`, borderRadius: 6, overflow: "hidden", fontSize: 11 }}>
      {options.map((opt) => {
        const active = value === opt.key;
        const isDisabled = disabled?.(opt) ?? false;
        return (
          <button
            key={opt.key}
            type="button"
            disabled={isDisabled}
            onClick={() => onChange(opt.key)}
            style={{
              padding: "3px 10px",
              background: active ? PRIMARY : "transparent",
              color: isDisabled ? MUTED : active ? "#fff" : FG1,
              border: "none", cursor: isDisabled ? "not-allowed" : "pointer",
              fontWeight: active ? 600 : 400,
              opacity: isDisabled ? 0.5 : 1,
            }}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}
